import { computed } from "vue";
import { useRouter, useRoute } from "vue-router";

import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import { useGlobalStore } from "@/stores/globalStore";
import useApplePay from "@/components/forms/Payments/Worldpayapple/useApplePay";

export default () => {
  const route = useRoute();
  const router = useRouter();
  const globalStore = useGlobalStore();
  const identityQueueStore = useIdentityQueueStore();

  const { isApplePayAvailable } = useApplePay();
  const defaultData = {
    urlData: {},
    orderMode: null,
    disableUIKeys: {},
    areDefaultsFromUrl: false,
  };

  const checkAndMutateIfFiatAndCoinTypesAreValid = (
    key,
    value,
    fiatInputs,
    coinInputs
  ) => {
    if (key === "fiatType" && !fiatInputs.includes(value)) {
      const [newDefaultFiatValue] = fiatInputs;
      return newDefaultFiatValue;
    }

    if (key === "coinType" && !coinInputs.includes(value)) {
      const [newDefaultCoinValue] = coinInputs;
      return newDefaultCoinValue;
    }

    return "";
  };

  /**
   * Method binds business logic to push to the Vue form ref Object
   * and update the store using updateValue action.
   * @param {Object} data
   * @param {Ref Object} form
   * @param {Object} store
   * @returns {}
   * */
  const setFormRefsAndUpdateStore = async (
    merchApiData,
    form,
    store,
    fiatInputs,
    coinInputs
  ) => {
    if (merchApiData.fiatAmount === null && merchApiData.coinAmount === null) {
      merchApiData.fiatAmount = 100;
    }

    const orderData = Object.entries(merchApiData);
    const keysToRelatedToOrder = Object.keys(store.form);
    const keysRelatedToOtp = Object.keys(store.otpData);

    const storeHasNoErrors = computed(() => !store.formHasErrors);
    const { redirectTo } = merchApiData;
    const globalStore = useGlobalStore();
    // HE-1276: Container for initial payment method that is passed via B2B transaction.
    let orderDataPaymentMethod = null;

    orderData.forEach(([key, value]) => {
      if (keysRelatedToOtp.includes(key) && value) {
        store.otpData[key] = value;
        store.updateValue(key, value);
      }

      if (!keysToRelatedToOrder.includes(key) || !value) {
        if (key === "account_reference") {
          globalStore.setCustomerReference(value);
        }
        if (key === "lockFiat") {
          store.form.lockFiat.value = value;
        }
        return;
      }

      if (key === "payment") {
        orderDataPaymentMethod = value;
      }

      if (key === "fiatType" || key === "coinType") {
        const newUnSupportedDefaultValue =
          checkAndMutateIfFiatAndCoinTypesAreValid(
            key,
            value,
            fiatInputs,
            coinInputs
          );

        if (newUnSupportedDefaultValue) {
          store.updateValue(key, newUnSupportedDefaultValue);
          form.value[key] = newUnSupportedDefaultValue;
          store.form[key].fromApi = true;
          return;
        }
      }

      store.form[key].fromApi = true;
      store.form[key].value = value;
      form.value[key] = value;
    });

    let canSkipError = false;
    if (storeHasNoErrors.value) {
      canSkipError = true;
    }
    const errorInForm = store.getErrorInForm;
    if (
      store.form.walletAddress?.value !== "" &&
      errorInForm.length === 1 &&
      errorInForm[0] === "walletAddress"
    ) {
      // if wallet address is not empty, it should have been validated from BE. Therefore, it's fine to skip error in FE validation.
      canSkipError = true;
    }

    // HE-1276: We will check if the apple pay method is available from the APM/PSP choices.
    // PSP choices are already filtered via `removeApplepayByUserSession` in globalStore.js
    const isApplePayMethodPresent = globalStore.getPayments.find(
      (paymentMethod) => paymentMethod.code === orderDataPaymentMethod
    );
    // Check if Apple Pay is available in browser and check if the payment method is available via B2C at the same time check for null/undefined value on order data payment method
    if (
      !isApplePayAvailable() &&
      !isApplePayMethodPresent &&
      orderDataPaymentMethod &&
      store.$id === "buyOrder"
    ) {
      return;
    }

    if (
      !redirectTo ||
      redirectTo === "order" ||
      !canSkipError ||
      globalStore.getMerchantSettings?.referredWalletAddressIsEditable
    ) {
      return;
    }

    if (redirectTo.indexOf("verification") >= 0) {
      router.push(`/${redirectTo}`);
      return;
    }

    await identityQueueStore.fetchIdentityDocuments();
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });  };

  /**
   * Sifts through the api order data to find relevant keys' values
   * filled and if so, then to provide a Boolean for that field to be
   * disabled, and set within the reduced object to be returned.
   *
   * @param {Array} orderData (Array passed through Object.entries)
   * @param {Function} callback
   * @returns {Object}
   * */

  const disableUiByApi = (merchApiData) => {
    const keysRelatedToApiToDisableUiBy = [
      "coinType",
      "walletAddress",
      "walletAddressTag",
      "blockchain",
    ];

    if (globalStore?.global?.merchant?.readOnlyAmounts) {
      keysRelatedToApiToDisableUiBy.push("coinAmount");
      keysRelatedToApiToDisableUiBy.push("fiatAmount");
      keysRelatedToApiToDisableUiBy.push("fiatType");
    }
    const orderData = Object.entries(merchApiData);

    return orderData.reduce((acc, [key, value]) => {
      if (
        !keysRelatedToApiToDisableUiBy.includes(key) ||
        (!["coinAmount", "fiatAmount"].includes(key) && !value)
      ) {
        return acc;
      }

      if (
        globalStore.getMerchantSettings?.referredWalletAddressIsEditable &&
        key.includes("walletAddress")
      ) {
        return acc;
      }

      acc[key] = true;
      return acc;
    }, {});
  };

  return {
    setFormRefsAndUpdateStore,
    disableUiByApi,
    defaultData,
  };
};
